import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  ConfigService,
  isErrorHandledByInterceptor,
  isNetworkError,
} from '@orion-platform/core';
import { Column } from '@orion-platform/datagrid';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { NotificationService } from 'libs/core/src/lib/services';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { EnumGoodsReceiptsFieldStructure, GoodsReceiptsList } from '../models/goods-receipts.model';
import { EnumInvoicingFieldStructure, InvoicingList } from '../models/invoicing.model';
import { GOODS_RECEIPTS_LIST } from '../pages/financial-module/goods-receipts/goods-receipts-list/goods-receipts-list.consts';
import { PurchaseOrganization } from '../pages/financial-module/invoice-reconciliation/invoice-reconciliation.types';
import {
  INVOICING_COLUMN_LIST,
  INVOICING_FIELDS_KEY,
} from '../pages/financial-module/invoicing/invoicing.consts';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { reconcileColumns } from '../utils/columns.utils';
import {
  DeleteInvoiceFlags,
  SetInvoiceReviewed,
} from '../store/financial-module/financial-module.action';
import {
  DEFAULT_INVOICE_SORTING_FOR_DOWNLOAD,
  flagInvoiceIdentifierField,
  INVOICE_DOWNLOAD_IDENTIFIER,
  INVOICE_FLAGS_LIST,
  INVOICE_RECONCILIATION_FIELDS,
  INVOICE_STATUS_LIST,
  PURCHASING_ORGANISATIONS_LIST,
} from '../store/financial-module/financial-module.consts';
import {
  GetInvoiceByFilterDto,
  GoodsReceiptDto,
  GoodsReceiptFileDto,
  GoodsReceiptFilterDto,
  InvoiceFilterDto,
  InvoiceReconciliationDto,
  InvoiceReconciliationFilterDto,
  InvoiceReviewed,
  InvoiceStatusDto,
  InvoicingData,
} from '../store/financial-module/financial-module.types';
import { buildAllowedSet, buildFallbackColumns } from '../store/slots/slots.utils';
import { Plants } from '../types';
import { ColumnsState } from '../types/columns.types';
import { formatParams, getUrl, isMulti } from '../utils/general.utils';
import { ColumnsService } from './columns.service';
import { DefaultFormSettingsService } from './default-form-settings.service';
import { SupplyVisibilityService } from './supply-visibility.service';

/** BE response shape for both purchase-organization endpoints. */
export type PurchaseOrganizationDto = {
  description?: null | string;
  name: string;
};

@Injectable({
  providedIn: 'root',
})
export class FinancialModuleService {
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly supplyVisibility = inject(SupplyVisibilityService);
  private readonly columns = inject(ColumnsService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly notificationService = inject(NotificationService);

  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  private readonly plant$ = this.store
    .select(AuthenticationSelectors.activePlant)
    .pipe(filter(Boolean), shareReplay({ bufferSize: 1, refCount: true }));

  private readonly financialModulesUrl = getUrl(this.baseUrl, 'api/v1/financialmodules');

  private invoicingUrl(plant: Plants): string {
    return `${this.financialModulesUrl}/${isMulti(plant) ? 'InvoicingOrch' : 'Invoicing'}`;
  }

  private goodsReceiptsUrl(plant: Plants): string {
    return `${this.financialModulesUrl}/${isMulti(plant) ? 'GoodsReceiptsOrch' : 'GoodsReceipts'}`;
  }

  private invoiceReconciliationUrl(): string {
    return `${this.financialModulesUrl}/InvoiceReconciliation`;
  }

  private withPlant<T>(factory: (plant: Plants) => Observable<T>): Observable<T> {
    return this.plant$.pipe(
      take(1),
      switchMap((plant): Observable<T> => factory(plant as Plants)),
    );
  }

  private withInvoicingUrl<T>(fn: (url: string, plant: Plants) => Observable<T>): Observable<T> {
    return this.withPlant((plant): Observable<T> => fn(this.invoicingUrl(plant), plant));
  }

  private withGoodsReceiptsUrl<T>(
    fn: (url: string, plant: Plants) => Observable<T>,
  ): Observable<T> {
    return this.withPlant((plant): Observable<T> => fn(this.goodsReceiptsUrl(plant), plant));
  }

  private filteredData!: { invoiceNumbers: string };
  public getDetailFormKey: string[] = [];
  public formKey: string[] = [];

  getInvoicingData(
    body: InvoiceFilterDto,
    query?: ApiQueryParams,
  ): Observable<HttpResponse<InvoicingData>> {
    return this.withInvoicingUrl((url): Observable<HttpResponse<InvoicingData>> =>
      this.http.post<InvoicingData>(url, body, {
        observe: 'response',
        params: formatParams(query),
      }),
    );
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.withPlant((plant): Observable<string[]> =>
      this.defaultForms.formKeys(this.invoicingUrl(plant)),
    );
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.withPlant((plant): Observable<FormSetting[]> =>
      this.defaultForms.getFormSettings(this.invoicingUrl(plant), formKey),
    );
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.withPlant((plant): Observable<string[]> =>
      this.defaultForms.getFields(this.invoicingUrl(plant), formKey),
    );
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.withPlant((plant): Observable<FormSetting[]> =>
      this.defaultForms.update(this.invoicingUrl(plant), formKey, payload),
    );
  }

  // TODO not user anywhere deploy or remove?
  /**
   * The goods-receipts columns: the application's own set, narrowed to what the
   * form configuration allows, with the user's saved choice merged over it.
   *
   * Merged rather than simply preferred, so a set saved before the defaults
   * changed still works — columns that no longer exist drop out, new ones
   * appear, and whatever the user hid stays hidden. A failed lookup answers
   * with the defaults: a grid with the wrong columns beats no grid at all.
   * @returns {Observable<{ columns: Column[]; defaultColumns: boolean }>} The columns, and whether they are still the defaults.
   */
  getGoodsReceiptsDefaultColumns(): Observable<{ columns: Column[]; defaultColumns: boolean }> {
    const allDefaults = new GoodsReceiptsList(EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable)
      .fields as unknown as Column[];

    return this.getDefaultFormSettingFields(GOODS_RECEIPTS_LIST.fieldsKey).pipe(
      switchMap(
        (formFields: string[]): Observable<{ columns: Column[]; defaultColumns: boolean }> => {
          const allowed = buildAllowedSet(formFields);
          const permitted = allDefaults.filter((column): boolean =>
            allowed.has(column.field.toLowerCase()),
          );
          const fallback = buildFallbackColumns(permitted, formFields);

          return this.supplyVisibility
            .getUserSettings(GOODS_RECEIPTS_LIST.columnVisibilityKey)
            .pipe(
              map((settings): { columns: Column[]; defaultColumns: boolean } =>
                settings.data
                  ? {
                      columns: reconcileColumns(permitted, JSON.parse(settings.data) as Column[]),
                      defaultColumns: false,
                    }
                  : { columns: fallback, defaultColumns: true },
              ),
              catchError((): Observable<{ columns: Column[]; defaultColumns: boolean }> =>
                of({ columns: fallback, defaultColumns: true }),
              ),
            );
        },
      ),
    );
  }

  /**
   * The invoicing columns: the application's own set for this plant, with the
   * user's saved choice merged over it.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  getInvoicingDefaultColumns(): Observable<ColumnsState> {
    return this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new InvoicingList(EnumInvoicingFieldStructure.InvoicingTable, formFields)
          .fields as unknown as Column[],
      columnListKey: INVOICING_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(INVOICING_FIELDS_KEY),
    });
  }

  getGoodsReceiptsData(params: GoodsReceiptFilterDto): Observable<GoodsReceiptDto[]> {
    return this.withGoodsReceiptsUrl((url): Observable<GoodsReceiptDto[]> =>
      this.http.post<GoodsReceiptDto[]>(url, params),
    );
  }

  getGoodsReceiptsFile(params: GoodsReceiptFileDto, fields: string[]): Observable<Blob> {
    return this.withGoodsReceiptsUrl((url): Observable<Blob> =>
      this.http.post(
        `${url}/GetXlsx`,
        {
          ...params,
          Fields: fields,
          Sorts: this.defaultInvoiceSortingForDownload,
        },
        { responseType: 'blob' },
      ),
    );
  }

  getInvoiceStatus(inboundDeliveryNumber: string): Observable<InvoiceStatusDto[]> {
    return this.withInvoicingUrl((url): Observable<InvoiceStatusDto[]> =>
      this.http.get<InvoiceStatusDto[]>(`${url}/${inboundDeliveryNumber}/Status`),
    );
  }

  getInvoiceReconciliation(
    body: InvoiceReconciliationFilterDto,
  ): Observable<InvoiceReconciliationDto[]> {
    return this.http.post<InvoiceReconciliationDto[]>(this.invoiceReconciliationUrl(), body);
  }

  // ORB-7160: return the full purchase-organization objects (name + optional
  // description) instead of mapping to names, so the invoicing filter can show
  // "name | description" in the dropdown. Name-only consumers derive it.
  getInvoicingPurchaseOrganizations(): Observable<PurchaseOrganizationDto[]> {
    return this.withInvoicingUrl((url): Observable<PurchaseOrganizationDto[]> =>
      this.http.get<PurchaseOrganizationDto[]>(`${url}/purchaseorganizations`),
    );
  }

  getInvoiceReconciliationPurchaseOrganizations(): Observable<PurchaseOrganization[]> {
    return this.http
      .get<PurchaseOrganizationDto[]>(`${this.invoiceReconciliationUrl()}/purchaseorganizations`)
      .pipe(
        map((data): PurchaseOrganization[] =>
          data.map((item): PurchaseOrganization => ({
            purchaseCode: item.name,
            purchaseName: item.description ? `${item.name} | ${item.description}` : item.name,
          })),
        ),
      );
  }

  getInvoiceReconciliationXlsx(params: Record<string, string | string[]>): Observable<Blob> {
    return this.http.post(
      `${this.invoiceReconciliationUrl()}/GetXlsx`,
      {
        ...params,
        Fields: this.invoiceReconciliationFields,
        Sorts: this.defaultInvoiceSortingForDownload,
      },
      { responseType: 'blob' },
    );
  }

  invoicePdfDownload(invoiceIdentifier: string): Observable<Blob> {
    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/GetPdf`,
        { [this.invoiceDownloadIdentifierField.label]: [invoiceIdentifier] },
        'PDF',
      ),
    );
  }

  invoiceXlsxDownload(
    invoiceIdentifier: string | string[],
    additionalParams: Record<string, unknown> = {},
    loadDeatils?: boolean,
  ): Observable<Blob> {
    const ids = Array.isArray(invoiceIdentifier) ? invoiceIdentifier : [invoiceIdentifier];

    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/GetXlsx`,
        {
          Fields: this.getInvoiceFields([], [], true),
          LoadDetails: loadDeatils,
          Sorts: this.defaultInvoiceSortingForDownload,
          [this.invoiceDownloadIdentifierField.label]: ids,
          ...additionalParams,
        },
        'XLSX',
      ),
    );
  }

  invoiceXmlDownload(invoiceIdentifier: string): Observable<Blob> {
    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/InvoiceXml`,
        {
          Fields: this.getInvoiceFields(),
          [this.invoiceDownloadIdentifierField.label]: [invoiceIdentifier],
        },
        'XML',
      ),
    );
  }

  downloadInvoicesPdfMultiple(invoiceIdentifiers: string[]): Observable<Blob> {
    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/MultiInvoicePdf`,
        { [this.invoiceDownloadIdentifierField.label]: invoiceIdentifiers },
        'PDF (multiple)',
      ),
    );
  }

  downloadInvoicesXmlMultiple(invoiceIdentifiers: string[]): Observable<Blob> {
    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/InvoiceXml`,
        {
          Fields: this.getInvoiceFields(),
          [this.invoiceDownloadIdentifierField.label]: invoiceIdentifiers,
        },
        'XML (multiple)',
      ),
    );
  }

  downloadInvoicesXlsxMultiple(
    invoiceIdentifiers: string[],
    loadDeatils?: boolean,
  ): Observable<Blob> {
    return this.withInvoicingUrl((url): Observable<Blob> =>
      this.invoiceDownloadPost(
        `${url}/MultiInvoiceXlsx`,
        {
          Fields: this.getInvoiceFields([], [], true, true),
          LoadDetails: loadDeatils,
          Sorts: this.defaultInvoiceSortingForDownload,
          [this.invoiceDownloadIdentifierField.label]: invoiceIdentifiers,
        },
        'XLSX (multiple)',
      ),
    );
  }

  setInvoiceReviewed(invoiceReviewed: InvoiceReviewed): Observable<boolean> {
    return this.withInvoicingUrl((url): Observable<boolean> =>
      this.http.put<boolean>(
        `${url}/${invoiceReviewed[flagInvoiceIdentifierField]}/Flags/${invoiceReviewed.flag}`,
        null,
      ),
    );
  }

  deleteInvoiceFlags(invoiceIdentifier: string, flag: string): Observable<boolean> {
    return this.withInvoicingUrl((url): Observable<boolean> =>
      this.http.delete<boolean>(`${url}/${invoiceIdentifier}/Flags/${flag}`),
    );
  }

  setMarkInvoiceReviewed(
    reviewFlag: string,
    invoiceIdentifier: string,
    isFlagAvailable?: boolean,
  ): void {
    if (!reviewFlag) return;

    if (!isFlagAvailable) {
      const invoiceData = {
        flag: reviewFlag,
        [flagInvoiceIdentifierField]: invoiceIdentifier,
      };
      this.store.dispatch(new SetInvoiceReviewed(invoiceData));
      return;
    }

    this.store.dispatch(new DeleteInvoiceFlags(invoiceIdentifier, reviewFlag));
  }

  getInvoiceByFilter(filter: GetInvoiceByFilterDto): Observable<InvoicingData> {
    return this.withInvoicingUrl((url): Observable<InvoicingData> =>
      this.http.post<InvoicingData>(`${url}/GetInvoiceByFilter`, filter),
    );
  }

  getFilterAdditionsForInvoice(): Observable<string[]> {
    return this.withInvoicingUrl((url): Observable<string[]> =>
      this.http.get<string[]>(`${url}/GetInvoiceByFilterAdditions`),
    );
  }

  setFilteredData(data: { invoiceNumbers: string }): void {
    this.filteredData = data;
  }

  getFilteredData(): { invoiceNumbers: string } {
    return this.filteredData;
  }

  getInvoiceFields(
    fieldsToAdd: string[] = [],
    fieldsToRemove: string[] = [],
    isXLSX = false,
    isZip = false,
  ): string[] {
    const fields = (isXLSX && !isZip ? this.formKeyData : this.invoiceFieldsForDownload).slice();

    for (const f of fieldsToAdd) if (f && !fields.includes(f)) fields.push(f);
    for (const f of fieldsToRemove) {
      const idx = fields.indexOf(f);
      if (idx >= 0) fields.splice(idx, 1);
    }
    return fields;
  }

  getInvoiceStatusList(): string[] {
    return [...INVOICE_STATUS_LIST];
  }

  getFlagsList(): Array<{ id: string; name: string }> {
    return [...INVOICE_FLAGS_LIST];
  }

  getPurchasingOrganisationsList(): string[] {
    return [...PURCHASING_ORGANISATIONS_LIST];
  }

  private invoiceDownloadPost(url: string, body: unknown, type: string): Observable<Blob> {
    return this.http
      .post(url, body, { responseType: 'blob' })
      .pipe(catchError((error): Observable<Blob> => this.handleInvoiceDownloadError(error, type)));
  }

  private handleInvoiceDownloadError(error: HttpErrorResponse, type: string): Observable<never> {
    const alreadyToasted = isNetworkError(error) || isErrorHandledByInterceptor(error);

    if (error?.error instanceof Blob) {
      const reader = new FileReader();
      reader.onload = (): void => {
        try {
          const json = JSON.parse(reader.result as string);
          const errorMessage = json.detail || 'An error occurred';
          if (!alreadyToasted) {
            this.notificationService.showError(errorMessage);
          }

          console.error(`Error occurred while downloading invoice ${type}:`, errorMessage);
        } catch (e) {
          console.error(`Error parsing error response for invoice ${type}:`, e);
        }
      };
      reader.onerror = (): void => {
        console.error(`Error reading error response for invoice ${type}:`, reader.error);
      };
      reader.readAsText(error.error);
    } else {
      console.error(`Error occurred while downloading invoice ${type}:`, error);
    }

    return throwError((): HttpErrorResponse => error);
  }

  setDetailData(data: string[] | undefined): void {
    this.getDetailFormKey = Array.isArray(data) ? data : [];
  }

  getDetailData(): string[] {
    return this.getDetailFormKey ?? [];
  }

  set formKeyData(data: string[]) {
    this.formKey = data;
  }

  get formKeyData(): string[] {
    return this.formKey;
  }

  get invoiceDownloadIdentifierField(): { label: string; value: string } {
    return INVOICE_DOWNLOAD_IDENTIFIER;
  }

  get invoiceFieldsForDownload(): string[] {
    return this.getDetailData();
  }

  get invoiceReconciliationFields(): string[] {
    return [...INVOICE_RECONCILIATION_FIELDS];
  }

  get defaultInvoiceSortingForDownload(): Array<{ Direction: string; Field: string }> {
    return [...DEFAULT_INVOICE_SORTING_FOR_DOWNLOAD];
  }
}
